<template>
  <div class="profile-container">
    <!-- 顶部导航 -->
    <div class="nav-bar">
      <div class="nav-content">
        <div class="logo">
          <span class="glow-text">云商城</span>
        </div>
        <div class="nav-menu">
          <el-menu mode="horizontal" default-active="0" class="nav-menu-inner">
            <el-menu-item index="1" @click="goToHome">
              <el-icon><HomeFilled /></el-icon>
              返回首页
            </el-menu-item>
          </el-menu>
        </div>
        <div class="user-info">
          <el-dropdown @command="handleCommand">
            <span class="el-dropdown-link">
              {{ userNickname || '用户' }}
              <el-icon class="el-icon--right"><ArrowDown /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="home">返回首页</el-dropdown-item>
                <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="main-content">
      <div class="profile-wrapper">
        <!-- 左侧头像区域 -->
        <div class="profile-sidebar glassmorphism">
          <div class="avatar-section">
            <div class="avatar-ring">
              <el-avatar :size="100" :src="avatarUrl" class="user-avatar">
                <el-icon :size="48"><UserFilled /></el-icon>
              </el-avatar>
            </div>
            <h2 class="sidebar-nickname">{{ form.nickname || '未设置昵称' }}</h2>
            <p class="sidebar-username">@{{ form.username }}</p>
            <el-tag :type="form.status === 1 ? 'success' : 'danger'" effect="dark" round>
              {{ form.status === 1 ? '正常' : '已禁用' }}
            </el-tag>
          </div>
          <div class="sidebar-stats">
            <div class="stat-item">
              <span class="stat-value">{{ form.orderCount || 0 }}</span>
              <span class="stat-label">订单</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ form.cartCount || 0 }}</span>
              <span class="stat-label">购物车</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ form.favoriteCount || 0 }}</span>
              <span class="stat-label">收藏</span>
            </div>
          </div>
          <div class="sidebar-actions">
            <button class="btn-back" @click="goToHome">
              <el-icon><ArrowLeft /></el-icon>
              返回商城首页
            </button>
          </div>
        </div>

        <!-- 右侧资料编辑区域 -->
        <div class="profile-main glassmorphism">
          <div class="section-header">
            <h2>
              <el-icon><Setting /></el-icon>
              个人资料
            </h2>
            <span class="header-tip">管理您的个人信息</span>
          </div>

          <el-form 
            ref="formRef" 
            :model="form" 
            :rules="rules" 
            label-width="100px" 
            label-position="left"
            class="profile-form"
            :disabled="!isEditing"
          >
            <div class="form-group-title">
              <el-icon><User /></el-icon>
              基本信息
            </div>

            <el-form-item label="用户名" prop="username">
              <el-input v-model="form.username" disabled placeholder="用户名不可修改">
                <template #prefix>
                  <el-icon><User /></el-icon>
                </template>
              </el-input>
            </el-form-item>

            <el-form-item label="昵称" prop="nickname">
              <el-input v-model="form.nickname" placeholder="请输入昵称" maxlength="20" show-word-limit>
                <template #prefix>
                  <el-icon><EditPen /></el-icon>
                </template>
              </el-input>
            </el-form-item>

            <el-form-item label="邮箱" prop="email">
              <el-input v-model="form.email" placeholder="请输入邮箱地址" maxlength="50">
                <template #prefix>
                  <el-icon><Message /></el-icon>
                </template>
              </el-input>
            </el-form-item>

            <el-form-item label="手机号" prop="phone">
              <el-input v-model="form.phone" placeholder="请输入手机号" maxlength="11">
                <template #prefix>
                  <el-icon><Phone /></el-icon>
                </template>
              </el-input>
            </el-form-item>

            <div class="form-group-title">
              <el-icon><InfoFilled /></el-icon>
              更多信息
            </div>

            <el-form-item label="性别" prop="gender">
              <el-radio-group v-model="form.gender">
                <el-radio :value="1">男</el-radio>
                <el-radio :value="2">女</el-radio>
                <el-radio :value="0">保密</el-radio>
              </el-radio-group>
            </el-form-item>

            <el-form-item label="个人简介" prop="bio">
              <el-input 
                v-model="form.bio" 
                type="textarea" 
                :rows="3"
                placeholder="介绍一下自己吧..."
                maxlength="200"
                show-word-limit
              />
            </el-form-item>

            <el-form-item label="收货地址" prop="address">
              <el-input 
                v-model="form.address" 
                type="textarea" 
                :rows="2"
                placeholder="请输入默认收货地址"
                maxlength="200"
              />
            </el-form-item>
          </el-form>

          <!-- 操作按钮区域 -->
          <div class="form-actions">
            <template v-if="!isEditing">
              <button class="btn-edit" @click="startEdit">
                <el-icon><Edit /></el-icon>
                修改资料
              </button>
            </template>
            <template v-else>
              <button class="btn-save" @click="saveProfile" :disabled="saving">
                <el-icon v-if="!saving"><Check /></el-icon>
                <el-icon v-else class="loading-icon"><Loading /></el-icon>
                {{ saving ? '保存中...' : '保存修改' }}
              </button>
              <button class="btn-cancel" @click="cancelEdit">
                <el-icon><Close /></el-icon>
                取消修改
              </button>
            </template>
          </div>

          <!-- 密码修改区域 -->
          <div class="password-section">
            <div class="section-header" style="margin-top: 32px;">
              <h2>
                <el-icon><Lock /></el-icon>
                修改密码
              </h2>
              <span class="header-tip">定期修改密码以保护账户安全</span>
            </div>

            <el-form 
              ref="pwdFormRef" 
              :model="pwdForm" 
              :rules="pwdRules" 
              label-width="100px" 
              label-position="left"
              class="profile-form"
            >
              <el-form-item label="当前密码" prop="oldPassword">
                <el-input v-model="pwdForm.oldPassword" type="password" show-password placeholder="请输入当前密码">
                  <template #prefix>
                    <el-icon><Lock /></el-icon>
                  </template>
                </el-input>
              </el-form-item>

              <el-form-item label="新密码" prop="newPassword">
                <el-input v-model="pwdForm.newPassword" type="password" show-password placeholder="请输入新密码（至少6位）">
                  <template #prefix>
                    <el-icon><Key /></el-icon>
                  </template>
                </el-input>
              </el-form-item>

              <el-form-item label="确认密码" prop="confirmPassword">
                <el-input v-model="pwdForm.confirmPassword" type="password" show-password placeholder="请再次输入新密码">
                  <template #prefix>
                    <el-icon><Key /></el-icon>
                  </template>
                </el-input>
              </el-form-item>
            </el-form>

            <div class="form-actions">
              <button class="btn-edit" @click="changePassword" :disabled="changingPwd">
                <el-icon v-if="!changingPwd"><Check /></el-icon>
                <el-icon v-else class="loading-icon"><Loading /></el-icon>
                {{ changingPwd ? '提交中...' : '修改密码' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部 -->
    <div class="footer">
      <div class="footer-content">
        <div class="footer-section">
          <h4>关于我们</h4>
          <p>云商城是一个基于微服务架构的现代化交易平台。</p>
        </div>
        <div class="footer-section">
          <h4>快速链接</h4>
          <ul>
            <li><a href="#">帮助中心</a></li>
            <li><a href="#">联系我们</a></li>
            <li><a href="#">服务条款</a></li>
          </ul>
        </div>
        <div class="footer-section">
          <h4>联系方式</h4>
          <p>Email: support@cloudshop.com</p>
          <p>Phone: 400-800-8888</p>
        </div>
      </div>
      <div class="footer-bottom">
        <p>&copy; 2026 云商城 - 微服务交易平台. All rights reserved.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElNotification, ElMessageBox } from 'element-plus'
import { 
  ArrowDown, ArrowLeft, HomeFilled, UserFilled, User, EditPen, 
  Message, Phone, InfoFilled, Setting, Edit, Check, Close, 
  Lock, Key, Loading 
} from '@element-plus/icons-vue'
import authAPI from '../api/auth'

const router = useRouter()
const formRef = ref(null)
const pwdFormRef = ref(null)
const userNickname = ref('')
const isEditing = ref(false)
const saving = ref(false)
const changingPwd = ref(false)
const avatarUrl = ref('')

// 原始数据备份（用于取消编辑时还原）
let originalForm = {}

const form = reactive({
  username: '',
  nickname: '',
  email: '',
  phone: '',
  gender: 0,
  bio: '',
  address: '',
  status: 1,
  orderCount: 0,
  cartCount: 0,
  favoriteCount: 0,
})

const pwdForm = reactive({
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
})

const rules = {
  nickname: [
    { required: true, message: '请输入昵称', trigger: 'blur' },
    { min: 2, max: 20, message: '昵称长度在2到20个字符之间', trigger: 'blur' },
  ],
  email: [
    { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' },
  ],
  phone: [
    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' },
  ],
}

const pwdRules = {
  oldPassword: [
    { required: true, message: '请输入当前密码', trigger: 'blur' },
  ],
  newPassword: [
    { required: true, message: '请输入新密码', trigger: 'blur' },
    { min: 6, max: 20, message: '密码长度在6到20个字符之间', trigger: 'blur' },
  ],
  confirmPassword: [
    { required: true, message: '请再次输入新密码', trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        if (value !== pwdForm.newPassword) {
          callback(new Error('两次输入的密码不一致'))
        } else {
          callback()
        }
      },
      trigger: 'blur',
    },
  ],
}

/** 返回首页 */
const goToHome = () => {
  router.push('/home')
}

/** 用户下拉菜单命令 */
const handleCommand = (command) => {
  if (command === 'logout') {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    ElNotification({
      title: '再见',
      message: '已成功退出登录，欢迎再次使用云商城！',
      type: 'info',
      duration: 1800,
    })
    router.push('/login')
  } else if (command === 'home') {
    goToHome()
  }
}

/** 开始编辑 */
const startEdit = () => {
  // 备份当前数据
  originalForm = { ...form }
  isEditing.value = true
}

/** 取消编辑 */
const cancelEdit = () => {
  // 还原数据
  Object.assign(form, originalForm)
  isEditing.value = false
  ElMessage.info('已取消修改')
}

/** 保存资料 */
const saveProfile = async () => {
  if (!formRef.value) return

  try {
    await formRef.value.validate()
  } catch {
    ElMessage.warning('请检查表单填写是否正确')
    return
  }

  saving.value = true
  try {
    const updateData = {
      nickname: form.nickname,
      email: form.email,
      phone: form.phone,
      gender: form.gender,
      bio: form.bio,
      address: form.address,
    }

    await authAPI.put('/user/profile', updateData)

    // 同步更新本地存储
    const userStr = localStorage.getItem('user')
    if (userStr) {
      const user = JSON.parse(userStr)
      user.nickname = form.nickname
      user.email = form.email
      user.phone = form.phone
      user.gender = form.gender
      user.bio = form.bio
      user.address = form.address
      localStorage.setItem('user', JSON.stringify(user))
      userNickname.value = form.nickname || form.username
    }

    isEditing.value = false
    ElNotification({
      title: '保存成功',
      message: '个人资料已更新',
      type: 'success',
      duration: 2000,
    })
  } catch (error) {
    console.error('保存资料失败:', error)
    const errMsg = error.response?.data?.message || error.message || '保存失败，请重试'
    // 即使后端接口不存在，也将数据保存到本地
    const userStr = localStorage.getItem('user')
    if (userStr) {
      const user = JSON.parse(userStr)
      user.nickname = form.nickname
      user.email = form.email
      user.phone = form.phone
      user.gender = form.gender
      user.bio = form.bio
      user.address = form.address
      localStorage.setItem('user', JSON.stringify(user))
      userNickname.value = form.nickname || form.username
    }
    isEditing.value = false
    ElNotification({
      title: '已本地保存',
      message: '资料已保存到本地（后端接口暂不可用）',
      type: 'warning',
      duration: 2500,
    })
  } finally {
    saving.value = false
  }
}

/** 修改密码 */
const changePassword = async () => {
  if (!pwdFormRef.value) return

  try {
    await pwdFormRef.value.validate()
  } catch {
    ElMessage.warning('请检查密码填写是否正确')
    return
  }

  await ElMessageBox.confirm('确认修改密码？修改后需要重新登录。', '提示', {
    confirmButtonText: '确认修改',
    cancelButtonText: '取消',
    type: 'warning',
  })

  changingPwd.value = true
  try {
    await authAPI.put('/user/password', {
      oldPassword: pwdForm.oldPassword,
      newPassword: pwdForm.newPassword,
    })

    ElNotification({
      title: '密码已修改',
      message: '密码修改成功，请重新登录',
      type: 'success',
      duration: 2000,
    })

    // 清除登录状态并跳转
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    setTimeout(() => {
      router.push('/login')
    }, 1500)
  } catch (error) {
    console.error('修改密码失败:', error)
    const errMsg = error.response?.data?.message || error.message || '修改密码失败，请重试'
    ElMessage.error(errMsg)
  } finally {
    changingPwd.value = false
  }
}

/** 加载用户资料 */
const loadProfile = () => {
  const userStr = localStorage.getItem('user')
  if (userStr) {
    try {
      const user = JSON.parse(userStr)
      form.username = user.username || ''
      form.nickname = user.nickname || ''
      form.email = user.email || ''
      form.phone = user.phone || ''
      form.gender = user.gender || 0
      form.bio = user.bio || ''
      form.address = user.address || ''
      form.status = user.status ?? 1
      userNickname.value = user.nickname || user.username || '用户'
    } catch (e) {
      console.error('解析用户信息失败:', e)
    }
  }
}

/** 尝试从后端拉取最新的用户资料 */
const fetchProfile = async () => {
  try {
    const response = await authAPI.get('/user/profile')
    const data = response.data
    if (data) {
      const user = data.data || data
      form.username = user.username || form.username
      form.nickname = user.nickname || form.nickname
      form.email = user.email || form.email
      form.phone = user.phone || form.phone
      form.gender = user.gender ?? form.gender
      form.bio = user.bio || form.bio
      form.address = user.address || form.address
      form.status = user.status ?? form.status
      form.orderCount = user.orderCount || 0
      form.cartCount = user.cartCount || 0
      form.favoriteCount = user.favoriteCount || 0
      userNickname.value = form.nickname || form.username || '用户'

      // 同步到本地
      localStorage.setItem('user', JSON.stringify({
        ...JSON.parse(localStorage.getItem('user') || '{}'),
        ...user,
      }))
    }
  } catch (error) {
    console.log('后端资料接口不可用，使用本地数据:', error.message)
  }
}

onMounted(async () => {
  loadProfile()
  await fetchProfile()
})
</script>

<style scoped>
.profile-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #f1f4f8 100%);
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
}

/* ========== 导航栏 ========== */
.nav-bar {
  background: linear-gradient(135deg, #232a4d 0%, #4b2067 100%);
  box-shadow: 0 2px 8px #0000150d;
  position: sticky;
  top: 0;
  z-index: 1000;
  backdrop-filter: blur(10px);
}

.nav-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.logo {
  font-size: 24px;
  font-weight: bold;
  margin-right: 40px;
  min-width: 120px;
}

.glow-text {
  color: #fff;
  text-shadow: 0 0 8px #a3a3ff, 0 0 16px #4b2067;
}

.nav-menu {
  flex: 1;
}

.nav-menu-inner {
  background: transparent !important;
  border-bottom: none !important;
}

.nav-menu-inner :deep(.el-menu-item) {
  color: #fff !important;
  background: transparent !important;
  font-size: 0.95rem;
}

.nav-menu-inner :deep(.el-menu-item:hover) {
  background: rgba(255, 255, 255, 0.1) !important;
}

.user-info {
  margin-left: auto;
  color: #fff;
  font-size: 14px;
}

.user-info :deep(.el-dropdown-link) {
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-info :deep(.el-dropdown-link:hover) {
  color: #a3a3ff;
}

/* ========== 主内容区 ========== */
.main-content {
  flex: 1;
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
  padding: 28px 16px;
  box-sizing: border-box;
}

.profile-wrapper {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

/* ========== 磨砂玻璃效果 ========== */
.glassmorphism {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 18px;
  box-shadow: 0 8px 32px rgba(31, 38, 135, 0.08);
}

/* ========== 左侧侧边栏 ========== */
.profile-sidebar {
  width: 280px;
  min-width: 280px;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.avatar-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.avatar-ring {
  padding: 4px;
  border-radius: 50%;
  background: linear-gradient(135deg, #537ce5 0%, #8b5fc9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-avatar {
  border: 3px solid #fff;
  background: linear-gradient(135deg, #e0e7ff 0%, #ede0ff 100%);
}

.sidebar-nickname {
  font-size: 1.25rem;
  font-weight: 700;
  color: #232a4d;
  margin: 0;
}

.sidebar-username {
  font-size: 0.85rem;
  color: #888;
  margin: 0;
}

.sidebar-stats {
  display: flex;
  gap: 20px;
  width: 100%;
  justify-content: center;
  padding: 16px 0;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.stat-value {
  font-size: 1.2rem;
  font-weight: 700;
  color: #537ce5;
}

.stat-label {
  font-size: 0.8rem;
  color: #999;
}

.sidebar-actions {
  width: 100%;
}

/* ========== 按钮公共样式 ========== */
.btn-back,
.btn-edit,
.btn-save,
.btn-cancel {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-back {
  background: linear-gradient(135deg, #232a4d 0%, #4b2067 100%);
  color: #fff;
}

.btn-back:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(35, 42, 77, 0.35);
}

.btn-edit {
  background: linear-gradient(135deg, #537ce5 0%, #8b5fc9 100%);
  color: #fff;
}

.btn-edit:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(83, 124, 229, 0.4);
}

.btn-edit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-save {
  background: linear-gradient(135deg, #43b581 0%, #2e8b57 100%);
  color: #fff;
}

.btn-save:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(46, 139, 87, 0.4);
}

.btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-cancel {
  background: rgba(0, 0, 0, 0.05);
  color: #666;
}

.btn-cancel:hover {
  background: rgba(0, 0, 0, 0.1);
  color: #333;
}

/* ========== 右侧主内容 ========== */
.profile-main {
  flex: 1;
  padding: 32px;
}

.section-header {
  margin-bottom: 24px;
}

.section-header h2 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.2rem;
  font-weight: 700;
  color: #232a4d;
  margin: 0 0 4px 0;
}

.header-tip {
  font-size: 0.85rem;
  color: #999;
  margin-left: 28px;
}

.form-group-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #537ce5;
  margin: 20px 0 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(83, 124, 229, 0.15);
}

.form-group-title:first-of-type {
  margin-top: 0;
}

.profile-form {
  max-width: 560px;
}

.profile-form :deep(.el-input__wrapper) {
  border-radius: 10px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08) inset;
  transition: all 0.3s ease;
}

.profile-form :deep(.el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px rgba(83, 124, 229, 0.3) inset;
}

.profile-form :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 2px rgba(83, 124, 229, 0.4) inset;
}

.profile-form :deep(.el-textarea__inner) {
  border-radius: 10px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08) inset;
  transition: all 0.3s ease;
}

.profile-form :deep(.el-textarea__inner:hover) {
  box-shadow: 0 0 0 1px rgba(83, 124, 229, 0.3) inset;
}

.profile-form :deep(.el-textarea__inner:focus) {
  box-shadow: 0 0 0 2px rgba(83, 124, 229, 0.4) inset;
}

.profile-form :deep(.el-form-item__label) {
  font-weight: 500;
  color: #555;
}

.profile-form :deep(.el-input.is-disabled .el-input__wrapper) {
  background: rgba(0, 0, 0, 0.03);
}

/* ========== 操作按钮组 ========== */
.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
  max-width: 560px;
}

.form-actions .btn-save,
.form-actions .btn-cancel {
  flex: 1;
}

.form-actions .btn-edit {
  width: auto;
  min-width: 160px;
}

/* ========== 密码区域 ========== */
.password-section {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  margin-top: 32px;
  padding-top: 8px;
}

/* ========== Loading动画 ========== */
.loading-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ========== 底部 ========== */
.footer {
  background: #232a4d;
  color: #fff;
  margin-top: auto;
  padding: 30px 20px 15px;
}

.footer-content {
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin-bottom: 25px;
}

.footer-section h4 {
  margin-bottom: 16px;
  font-size: 1.1rem;
}

.footer-section p {
  font-size: 0.9rem;
  opacity: 0.8;
  margin-bottom: 8px;
}

.footer-section ul {
  list-style: none;
  padding: 0;
}

.footer-section li {
  margin-bottom: 8px;
}

.footer-section a {
  color: #a3a3ff;
  text-decoration: none;
  transition: color 0.3s;
}

.footer-section a:hover {
  color: #fff;
}

.footer-bottom {
  border-top: 1px solid #4b206730;
  padding-top: 20px;
  text-align: center;
  font-size: 0.85rem;
  opacity: 0.7;
}

/* ========== 响应式设计 ========== */
@media (max-width: 768px) {
  .profile-wrapper {
    flex-direction: column;
  }

  .profile-sidebar {
    width: 100%;
    min-width: auto;
  }

  .profile-main {
    padding: 20px 16px;
  }

  .profile-form {
    max-width: 100%;
  }

  .form-actions {
    max-width: 100%;
    flex-direction: column;
  }

  .nav-content {
    flex-wrap: wrap;
    gap: 12px;
  }

  .nav-menu {
    width: 100%;
  }

  .user-info {
    width: 100%;
    margin-left: 0;
  }
}
</style>
